import React, { useState } from 'react';
import { Lock, Unlock, Eye, EyeOff, ShieldCheck, AlertCircle, ArrowRight } from 'lucide-react';
import { verifyPassword, setAuthenticated } from '../utils/auth';

interface LockScreenProps {
  onUnlock: () => void;
}

export const LockScreen: React.FC<LockScreenProps> = ({ onUnlock }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shake, setShake] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim() || isLoading) return;

    setIsLoading(true);
    setError(false);

    try {
      const isValid = await verifyPassword(password);
      if (isValid) {
        setAuthenticated(rememberMe);
        onUnlock();
      } else {
        setError(true);
        setShake(true);
        setTimeout(() => setShake(false), 500);
      }
    } catch (err) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 bg-[#080c14] ambient-mesh overflow-hidden">
      {/* Background glowing orbs */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-tiger-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Main Lock Card */}
      <div
        className={`w-full max-w-md glass-card rounded-2xl p-8 relative z-10 border border-slate-800/80 shadow-2xl backdrop-blur-xl transition-all duration-300 ${
          shake ? 'animate-shake border-red-500/60' : ''
        }`}
      >
        {/* Top Emblem & Badge */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="relative mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-tiger-600 via-tiger-500 to-amber-400 p-[1px] shadow-lg shadow-tiger-500/20 flex items-center justify-center">
              <div className="w-full h-full bg-slate-950/90 rounded-[15px] flex items-center justify-center text-tiger-400">
                <Lock className="w-8 h-8 text-tiger-400 stroke-[2.2]" />
              </div>
            </div>
            <span className="absolute -bottom-1 -right-1 flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-tiger-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-tiger-500 border-2 border-slate-950"></span>
            </span>
          </div>

          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900/90 border border-slate-700/60 text-xs font-medium text-slate-300 mb-3 shadow-inner">
            <ShieldCheck className="w-3.5 h-3.5 text-tiger-400" />
            <span>tiger-home.com gateway</span>
          </div>

          <h1 className="text-2xl font-bold tracking-tight text-white">
            Tiger Home Access
          </h1>
          <p className="text-sm text-slate-400 mt-1.5 max-w-xs">
            Private portal for hosted apps and micro-services. Enter the master access key to proceed.
          </p>
        </div>

        {/* Password Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
              Access Key / Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError(false);
                }}
                autoFocus
                placeholder="Enter access password..."
                className={`w-full bg-slate-900/90 border rounded-xl py-3 pl-4 pr-11 text-sm text-white placeholder-slate-500 focus:outline-none transition-all ${
                  error
                    ? 'border-red-500/80 focus:ring-2 focus:ring-red-500/30'
                    : 'border-slate-700/80 focus:border-tiger-500 focus:ring-2 focus:ring-tiger-500/20'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors"
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>

            {error && (
              <div className="flex items-center gap-1.5 text-xs text-red-400 pt-1 font-medium">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                <span>Incorrect access key. Please verify and try again.</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between text-xs">
            <label className="flex items-center gap-2 text-slate-400 hover:text-slate-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="rounded border-slate-700 bg-slate-900 text-tiger-500 focus:ring-tiger-500/20 focus:ring-offset-slate-900"
              />
              <span>Remember session on this device</span>
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading || !password.trim()}
            className="w-full relative group overflow-hidden rounded-xl bg-gradient-to-r from-tiger-600 via-tiger-500 to-amber-500 p-px font-semibold text-white shadow-lg shadow-tiger-500/20 transition-all hover:shadow-tiger-500/35 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="relative flex items-center justify-center gap-2 rounded-xl bg-slate-950/20 px-4 py-3 transition-colors group-hover:bg-transparent">
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <span>Unlock Gateway</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </div>
          </button>
        </form>

        {/* Footer info */}
        <div className="mt-8 pt-6 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-400 font-mono">
          <span>SECURE GATEWAY v1.0</span>
          <span>tiger-home.com</span>
        </div>
      </div>
    </div>
  );
};
